import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react'
import {WithChildren} from '../../../../_metronic/helpers'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import * as authHelper from './AuthHelpers'
import {AuthModel, UserModel} from './_models'
import {useInactivityTimer} from './useInactivityTimer'
import {AUTH_CONFIG} from './authConfig'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  resetInactivityTimer: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  resetInactivityTimer: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const logoutInProgressRef = useRef(false)

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = useCallback(() => {
    // Prevent multiple simultaneous logout calls
    if (logoutInProgressRef.current) return
    logoutInProgressRef.current = true

    saveAuth(undefined)
    setCurrentUser(undefined)
    localStorage.clear()

    // Navigate to login page using window.location for compatibility
    setTimeout(() => {
      window.location.href = '/auth/login'
      logoutInProgressRef.current = false
    }, 100)
  }, [])

  // Handle inactivity timeout
  const handleInactivityTimeout = useCallback(() => {
    console.log('Session expired due to inactivity')
    logout()
  }, [logout])

  // Initialize inactivity timer (only when user is logged in)
  const isLoggedIn = !!auth && !!localStorage.getItem('isLoggedIn')
  const {resetTimer: resetInactivityTimer} = useInactivityTimer({
    timeout: AUTH_CONFIG.INACTIVITY_TIMEOUT,
    onTimeout: handleInactivityTimeout,
    enabled: isLoggedIn,
  })

  return (
    <AuthContext.Provider
      value={{auth, saveAuth, currentUser, setCurrentUser, logout, resetInactivityTimer}}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(false)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    // const requestUser = async (apiToken: string) => {
    //   try {
    //     if (!didRequest.current) {
    //       const {data} = await getUserByToken(apiToken)
    //       if (data) {
    //         setCurrentUser(data)
    //       }
    //     }
    //   } catch (error) {
    //     console.error(error)
    //     if (!didRequest.current) {
    //       logout()
    //     }
    //   } finally {
    //     setShowSplashScreen(false)
    //   }
    //   return () => (didRequest.current = true)
    // }
    // if (auth && auth.api_token) {
    //   requestUser(auth.api_token)
    // } else {
    //   logout()
    //   setShowSplashScreen(false)
    // }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthInit, AuthProvider, useAuth}
